import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Plane,
  Cpu,
  FileText,
  Settings,
  Users,
  LogOut,
  Menu,
  X,
  UserCog
} from 'lucide-react';
import PersonalSettingsModal from './PersonalSettingsModal';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');

  useEffect(() => {
    if (user?.must_change_password) {
      setSettingsInitialTab('password');
      setSettingsOpen(true);
    }
  }, [user]);

  const openSettings = () => {
    setSettingsInitialTab('profile');
    setSettingsOpen(true);
  };

  const navigation = [
    { name: 'Operações', href: '/flights', icon: Plane },
    { name: 'Drones', href: '/drones', icon: Cpu },
    { name: 'Relatórios', href: '/reports', icon: FileText },
    { name: 'Empresa', href: '/settings', icon: Settings },
    ...(user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Usuários', href: '/admin/users', icon: Users }]
      : [])
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Mobile */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigation={navigation} isActive={isActive} user={user} logout={logout} openSettings={openSettings} />
        </div>
      </div>

      {/* Sidebar Desktop */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <SidebarContent navigation={navigation} isActive={isActive} user={user} logout={logout} openSettings={openSettings} />
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-10 lg:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
      <PersonalSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={settingsInitialTab} />
    </div>
  );
};

const SidebarContent = ({ navigation, isActive, user, logout, openSettings }) => (
  <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
    <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
      <div className="flex items-center flex-shrink-0 px-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <Plane className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="ml-3">
            <h1 className="text-lg font-semibold text-gray-900">Registro de Operações</h1>
          </div>
        </div>
      </div>
      <nav className="mt-8 flex-1 px-2 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                isActive(item.href)
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-200`}
            >
              <Icon
                className={`${
                  isActive(item.href) ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                } mr-3 h-5 w-5 transition-colors duration-200`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>
    </div>
    <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
      <div className="flex-shrink-0 w-full group block">
        <div className="flex items-center">
          <div>
            <div className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gray-500">
              <span className="text-sm font-medium leading-none text-white">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
              {user?.name}
            </p>
            <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
              {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Administrador' : 'Operador'}
            </p>
          </div>
          <div className="ml-3 flex space-x-2">
            <button
              onClick={openSettings}
              className="text-gray-400 hover:text-gray-500 transition-colors duration-200"
              title="Configurações pessoais"
            >
              <UserCog className="h-5 w-5" />
            </button>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-gray-500 transition-colors duration-200"
              title="Sair"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Layout;
